/**
 * Base class for import index pages (Monday imports and Study request imports)
 * Handles common functionality: checkboxes, bulk actions, retry import modal
 */
export default class BackofficeImportsBase {
    /**
     * @param {Object} config - Configuration object
     * @param {string} config.checkboxSelector - Selector for individual checkboxes
     * @param {string} config.checkboxAllSelector - Selector for "select all" checkbox
     * @param {string} config.bulkActionsContainerSelector - Selector for bulk actions container
     * @param {string} config.selectedCountSelector - Selector for selected count display
     * @param {string} config.bulkValidateSelector - Selector for bulk validate button
     * @param {string} config.bulkInvalidateSelector - Selector for bulk invalidate button
     * @param {string} config.bulkDeleteSelector - Selector for bulk delete button
     * @param {string} config.bulkArchiveSelector - Selector for bulk archive button
     * @param {string} config.bulkUnarchiveSelector - Selector for bulk unarchive button
     * @param {string} config.retryImportButtonSelector - Selector for retry import button
     * @param {string} config.retryImportModalId - ID of the retry import modal
     * @param {string} config.fileInputId - ID of the file input
     * @param {string} config.confirmCheckboxId - ID of the confirm checkbox
     * @param {string} config.confirmRetryButtonSelector - Selector for confirm retry button
     * @param {string} config.bulkValidateUrl - URL for bulk validate action
     * @param {string} config.bulkInvalidateUrl - URL for bulk invalidate action
     * @param {string} config.bulkDestroyUrl - URL for bulk destroy action
     * @param {string} config.bulkArchiveUrl - URL for bulk archive action
     * @param {string} config.bulkUnarchiveUrl - URL for bulk unarchive action
     * @param {string} config.retryImportUrl - URL for retry import action
     * @param {string} config.itemIdParamName - Parameter name for item IDs (e.g., 'order_ids[]' or 'study_request_ids[]')
     * @param {string} config.itemName - Human-readable item name (e.g., 'commande' or 'demande d\'étude')
     * @param {string} config.itemNamePlural - Human-readable item name plural (e.g., 'commande(s)' or 'demande(s) d\'étude')
     */
    constructor(config) {
        this.config = config;
        this.bindEvents();
        this.updateBulkActions();
    }

    bindEvents() {
        const {
            checkboxSelector,
            checkboxAllSelector,
            bulkValidateSelector,
            bulkInvalidateSelector,
            bulkDeleteSelector,
            bulkArchiveSelector,
            bulkUnarchiveSelector,
            retryImportButtonSelector,
            fileInputId,
            confirmCheckboxId,
            confirmRetryButtonSelector
        } = this.config;

        // Toggle individual checkbox
        $(document).on('change', checkboxSelector, () => {
            this.updateBulkActions();
        });

        // Toggle all checkboxes
        $(document).on('change', checkboxAllSelector, (event) => {
            const isChecked = $(event.target).prop('checked');
            $(checkboxSelector).prop('checked', isChecked);
            this.updateBulkActions();
        });

        // Bulk validate
        $(document).on('click', bulkValidateSelector, (event) => {
            event.preventDefault();
            this.bulkValidate();
        });

        // Bulk invalidate
        $(document).on('click', bulkInvalidateSelector, (event) => {
            event.preventDefault();
            this.bulkInvalidate();
        });

        // Bulk delete
        $(document).on('click', bulkDeleteSelector, (event) => {
            event.preventDefault();
            this.bulkDelete();
        });

        // Bulk archive
        $(document).on('click', bulkArchiveSelector, (event) => {
            event.preventDefault();
            this.bulkArchive();
        });

        // Bulk unarchive
        $(document).on('click', bulkUnarchiveSelector, (event) => {
            event.preventDefault();
            this.bulkUnarchive();
        });

        // Retry import
        $(document).on('click', retryImportButtonSelector, (event) => {
            event.preventDefault();
            this.showRetryImportModal();
        });

        // File input change
        $(document).on('change', `#${fileInputId}`, () => {
            this.validateImportForm();
        });

        // Confirm checkbox change
        $(document).on('change', `#${confirmCheckboxId}`, () => {
            this.validateImportForm();
        });

        // Confirm retry import button
        $(document).on('click', confirmRetryButtonSelector, (event) => {
            event.preventDefault();
            this.confirmRetryImport();
        });
    }

    updateBulkActions() {
        const { checkboxSelector, bulkActionsContainerSelector, selectedCountSelector } = this.config;
        const checkedBoxes = $(`${checkboxSelector}:checked`);
        const bulkContainer = $(bulkActionsContainerSelector);
        const selectedCount = $(selectedCountSelector);

        if (checkedBoxes.length > 0) {
            bulkContainer.show();
            selectedCount.text(checkedBoxes.length);
        } else {
            bulkContainer.hide();
            selectedCount.text(0);
        }
    }

    getSelectedIds() {
        const { checkboxSelector } = this.config;
        return $(`${checkboxSelector}:checked`).map(function() {
            return $(this).val();
        }).get();
    }

    bulkValidate() {
        const { bulkValidateUrl, itemName, itemNamePlural } = this.config;
        const ids = this.getSelectedIds();

        if (ids.length === 0) {
            application.errorNotification(`Aucune ${itemName} sélectionnée`);
            return;
        }

        if (confirm(`Êtes-vous sûr de vouloir valider ${ids.length} ${itemNamePlural} ?`)) {
            this.submitBulkAction(bulkValidateUrl, ids, 'PUT');
        }
    }

    bulkInvalidate() {
        const { bulkInvalidateUrl, itemName, itemNamePlural } = this.config;
        const ids = this.getSelectedIds();

        if (ids.length === 0) {
            application.errorNotification(`Aucune ${itemName} sélectionnée`);
            return;
        }

        if (confirm(`Êtes-vous sûr de vouloir invalider ${ids.length} ${itemNamePlural} ?`)) {
            this.submitBulkAction(bulkInvalidateUrl, ids, 'PUT');
        }
    }

    bulkDelete() {
        const { bulkDestroyUrl, itemName, itemNamePlural } = this.config;
        const ids = this.getSelectedIds();

        if (ids.length === 0) {
            application.errorNotification(`Aucune ${itemName} sélectionnée`);
            return;
        }

        if (confirm(`Êtes-vous sûr de vouloir supprimer ${ids.length} ${itemNamePlural} ? Cette action est irréversible. Seuls les éléments non validés seront supprimés.`)) {
            this.submitBulkAction(bulkDestroyUrl, ids, 'DELETE');
        }
    }

    bulkArchive() {
        const { bulkArchiveUrl, itemName, itemNamePlural } = this.config;
        const ids = this.getSelectedIds();

        if (ids.length === 0) {
            application.errorNotification(`Aucune ${itemName} sélectionnée`);
            return;
        }

        if (confirm(`Êtes-vous sûr de vouloir archiver ${ids.length} ${itemNamePlural} ? Seuls les éléments validés seront archivés.`)) {
            this.submitBulkAction(bulkArchiveUrl, ids, 'PUT');
        }
    }

    bulkUnarchive() {
        const { bulkUnarchiveUrl, itemName, itemNamePlural } = this.config;
        const ids = this.getSelectedIds();

        if (ids.length === 0) {
            application.errorNotification(`Aucune ${itemName} sélectionnée`);
            return;
        }

        if (confirm(`Êtes-vous sûr de vouloir désarchiver ${ids.length} ${itemNamePlural} ?`)) {
            this.submitBulkAction(bulkUnarchiveUrl, ids, 'PUT');
        }
    }

    submitBulkAction(url, ids, method) {
        const { itemIdParamName } = this.config;
        const form = $('<form>', {
            method: 'POST',
            action: url
        });

        // Add CSRF token
        const csrfToken = $('meta[name="csrf-token"]').attr('content');
        if (csrfToken) {
            form.append($('<input>', {
                type: 'hidden',
                name: 'authenticity_token',
                value: csrfToken
            }));
        }

        // Add method override for PUT/DELETE
        if (method !== 'POST') {
            form.append($('<input>', {
                type: 'hidden',
                name: '_method',
                value: method
            }));
        }

        // Add item IDs
        ids.forEach(id => {
            form.append($('<input>', {
                type: 'hidden',
                name: itemIdParamName,
                value: id
            }));
        });

        $('body').append(form);
        form.submit();
    }

    showRetryImportModal() {
        const { retryImportModalId } = this.config;
        $(`#${retryImportModalId}`).modal('show');
    }

    validateImportForm() {
        const { fileInputId, confirmCheckboxId, confirmRetryButtonSelector } = this.config;
        const fileInput = $(`#${fileInputId}`)[0];
        const confirmCheckbox = $(`#${confirmCheckboxId}`)[0];
        const submitButton = $(confirmRetryButtonSelector);

        let isValid = true;

        // Check if file is selected and is CSV
        if (!fileInput.files || fileInput.files.length === 0) {
            isValid = false;
        } else {
            const file = fileInput.files[0];
            if (!file.name.toLowerCase().endsWith('.csv') && !file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
                isValid = false;
                application.errorNotification('Seuls les fichiers CSV et Excel (.csv, .xlsx, .xls) sont acceptés');
            }
        }

        // Check if confirmation checkbox is checked
        if (!confirmCheckbox.checked) {
            isValid = false;
        }

        // Enable/disable submit button
        submitButton.prop('disabled', !isValid);
    }

    confirmRetryImport() {
        const { fileInputId, confirmCheckboxId, retryImportModalId, retryImportUrl } = this.config;
        const fileInput = $(`#${fileInputId}`)[0];

        if (!fileInput.files || fileInput.files.length === 0) {
            application.errorNotification('Veuillez sélectionner un fichier à importer');
            return;
        }

        const file = fileInput.files[0];
        if (!file.name.toLowerCase().endsWith('.csv') && !file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
            application.errorNotification('Seuls les fichiers CSV et Excel (.csv, .xlsx, .xls) sont acceptés');
            return;
        }

        if (!$(`#${confirmCheckboxId}`)[0].checked) {
            application.errorNotification('Veuillez confirmer l\'action');
            return;
        }

        // Hide modal first
        $(`#${retryImportModalId}`).modal('hide');

        // Create a form with file upload
        const form = $('<form>', {
            method: 'POST',
            action: retryImportUrl,
            enctype: 'multipart/form-data'
        });

        // Add CSRF token
        const csrfToken = $('meta[name="csrf-token"]').attr('content');
        if (csrfToken) {
            form.append($('<input>', {
                type: 'hidden',
                name: 'authenticity_token',
                value: csrfToken
            }));
        }

        // Add method override
        form.append($('<input>', {
            type: 'hidden',
            name: '_method',
            value: 'DELETE'
        }));

        // Add the file
        const fileInputClone = $(`#${fileInputId}`).clone();
        form.append(fileInputClone);

        $('body').append(form);
        form.submit();
    }

    onDataChanged(data) {
        // Custom logic for data changes if needed - can be overridden in subclasses
    }

    onDestroy() {
        const {
            checkboxSelector,
            checkboxAllSelector,
            bulkValidateSelector,
            bulkInvalidateSelector,
            bulkDeleteSelector,
            bulkArchiveSelector,
            bulkUnarchiveSelector,
            retryImportButtonSelector,
            fileInputId,
            confirmCheckboxId,
            confirmRetryButtonSelector
        } = this.config;

        // Cleanup event listeners
        $(document).off('change', checkboxSelector);
        $(document).off('change', checkboxAllSelector);
        $(document).off('click', bulkValidateSelector);
        $(document).off('click', bulkInvalidateSelector);
        $(document).off('click', bulkDeleteSelector);
        $(document).off('click', bulkArchiveSelector);
        $(document).off('click', bulkUnarchiveSelector);
        $(document).off('click', retryImportButtonSelector);
        $(document).off('change', `#${fileInputId}`);
        $(document).off('change', `#${confirmCheckboxId}`);
        $(document).off('click', confirmRetryButtonSelector);
    }
}
